/**
 * ============================================================================
 * VendorEvaluationScorecard — Phase 6d frontend (Supplier Management Module).
 * Owner: Claude 3 (Frontend & UX).
 * Route: /vendor-evaluations/:id
 * Purpose: full-screen scorecard editor. Header carries the supplier name +
 *          evaluation period + status. Big visual is a 4-axis weighted-score
 *          gauge. When the evaluation is IN_REVIEW the user can type the
 *          four axis scores (0–100) into numeric inputs; the weighted score
 *          and classification recompute on debounce. Signing routes through
 *          <SignAttestationDialog>.
 *
 * REGULATORY: VND-007 — APPROVED/CONDITIONAL needs one signer, DISQUALIFIED
 *  needs two signers + ≥200 char rationale.
 * ============================================================================ */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, AreaTrend, BarCompare } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './VendorEvaluationScorecard.module.css';
import { WeightedScoreGauge } from './WeightedScoreGauge';
import { SignAttestationDialog } from './SignAttestationDialog';
import { useEvaluationQuery, type EvaluationClassification, type HistoricalScorecard } from './useEvaluationQuery';
import { useComputeScorecardMutation } from './useComputeScorecardMutation';

const RATIONALE_MIN = 200;

export interface VendorEvaluationScorecardProps {
  evaluationId?: string;
}

export function VendorEvaluationScorecard({
  evaluationId = 'eval-demo',
}: VendorEvaluationScorecardProps = {}) {
  const { t, i18n } = useTranslation();
  const evalQ = useEvaluationQuery(evaluationId);
  const computeM = useComputeScorecardMutation();

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  // Local working copy of the four scores so the user can type freely; the
  // server is the source of truth for the weighted composite and the
  // classification.
  const [quality, setQuality] = useState<number>(0);
  const [delivery, setDelivery] = useState<number>(0);
  const [audit, setAudit] = useState<number>(0);
  const [service, setService] = useState<number>(0);
  const [rationale, setRationale] = useState<string>('');
  const [showSign, setShowSign] = useState(false);

  // Seed local state from the server payload the first time it arrives.
  useEffect(() => {
    const a = evalQ.data?.axis_scores ?? [];
    const find = (k: string) => a.find((x) => x.axis === k)?.score ?? 0;
    setQuality(find('QUALITY'));
    setDelivery(find('DELIVERY'));
    setAudit(find('AUDIT'));
    setService(find('SERVICE'));
    setRationale(evalQ.data?.rationale ?? '');
  }, [evalQ.data?.evaluation_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = evalQ.data;
  const inReview = data?.status === 'IN_REVIEW';
  const isSigned =
    data?.status === 'SIGNED_APPROVED' ||
    data?.status === 'SIGNED_CONDITIONAL' ||
    data?.status === 'SIGNED_DISQUALIFIED';

  // Compute a local weighted preview so the user sees their inputs land
  // immediately even before the server round-trip.
  const weightOf = (axis: string) =>
    data?.axis_scores.find((a) => a.axis === axis)?.weight ?? 0.25;
  const localWeighted =
    weightOf('QUALITY') * quality +
    weightOf('DELIVERY') * delivery +
    weightOf('AUDIT') * audit +
    weightOf('SERVICE') * service;

  // Local classification preview mirrors the server's default banding rules
  // (server still has final say). >=85 APPROVED, >=70 CONDITIONAL, else
  // DISQUALIFIED. We don't try to localize the bands — the server will
  // re-emit them on save.
  function localClassify(s: number): EvaluationClassification {
    if (s >= 85) return 'APPROVED';
    if (s >= 70) return 'CONDITIONAL';
    return 'DISQUALIFIED';
  }

  const localClass = localClassify(localWeighted);
  const isDisqualPreview = localClass === 'DISQUALIFIED';

  async function saveDraft() {
    await computeM.mutateAsync({
      evaluationId,
      quality,
      delivery,
      audit,
      service,
      rationale,
    });
    evalQ.refetch();
  }

  // ---- Performance charts: weighted-score trend (history + current) + axis breakdown ----
  const periodFmt = new Intl.DateTimeFormat(i18n.language, { month: 'short', year: '2-digit' });
  const trendData = (() => {
    const pts = (data?.history ?? [])
      .filter((h) => h.weighted_score != null)
      .map((h) => ({
        ts: new Date(h.period_start_utc).getTime(),
        period: periodFmt.format(new Date(h.period_start_utc)),
        score: Number(h.weighted_score),
      }));
    if (data?.weighted_score != null) {
      pts.push({
        ts: new Date(data.period_start_utc).getTime(),
        period: periodFmt.format(new Date(data.period_start_utc)),
        score: Number(data.weighted_score),
      });
    }
    return pts.sort((a, b) => a.ts - b.ts).map(({ period, score }) => ({ period, score }));
  })();
  const axisData = (() => {
    const a = data?.axis_scores ?? [];
    const find = (k: string) => Number(a.find((x) => x.axis === k)?.score ?? 0);
    return [
      { axis: t('supplier.eval.axis.quality', { defaultValue: 'Quality' }), score: find('QUALITY') },
      { axis: t('supplier.eval.axis.delivery', { defaultValue: 'Delivery' }), score: find('DELIVERY') },
      { axis: t('supplier.eval.axis.audit', { defaultValue: 'Audit' }), score: find('AUDIT') },
      { axis: t('supplier.eval.axis.service', { defaultValue: 'Service' }), score: find('SERVICE') },
    ];
  })();

  return (
    <div className={wstyles.adminScreen}>
      {evalQ.isLoading && (
        <Card>
          <CardBody>
            <div role="status" aria-live="polite">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={styles.skeletonRow} aria-hidden="true" />
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {evalQ.isError && (
        <Card>
          <CardBody>
            <div role="alert" className={styles.errorBanner}>
              <span>
                {t('supplier.eval.errors.loadFailed', {
                  defaultValue: 'Could not load the evaluation.',
                })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => evalQ.refetch()}
              >
                {t('common.retry', { defaultValue: 'Retry' })}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {!evalQ.isLoading && !evalQ.isError && data && (
        <>
          <header className={styles.evalHeader}>
            <div className={styles.evalHeaderMain}>
              <h1 className={styles.evalName}>{data.supplier_name}</h1>
              <p className={styles.evalPeriod}>
                {t('supplier.eval.period', {
                  defaultValue: 'Period: {{start}} — {{end}}',
                  start: dateFmt.format(new Date(data.period_start_utc)),
                  end: dateFmt.format(new Date(data.period_end_utc)),
                })}
              </p>
            </div>
            <span className={`${styles.statusBadge} ${styles[`status-${data.status}`] ?? ''}`}>
              {t(`supplier.eval.status.${data.status}`, {
                defaultValue: data.status,
              })}
            </span>
          </header>

          <Card>
            <CardBody>
              <WeightedScoreGauge
                axisScores={data.axis_scores}
                weightedScore={inReview ? localWeighted : data.weighted_score}
                classification={
                  inReview ? localClass : data.classification
                }
              />
            </CardBody>
          </Card>

          {inReview && (
            <Card>
              <CardHeader>
                <h2 className={wstyles.sectionTitle}>
                  {t('supplier.eval.formTitle', {
                    defaultValue: 'Axis scores',
                  })}
                </h2>
              </CardHeader>
              <CardBody>
                <form
                  className={styles.formGrid}
                  onSubmit={(e) => {
                    e.preventDefault();
                    void saveDraft();
                  }}
                  aria-label={t('supplier.eval.formAria', {
                    defaultValue: 'Vendor evaluation axis scores',
                  })}
                >
                  <AxisInput
                    id="eval-q"
                    label={t('supplier.eval.axis.QUALITY', {
                      defaultValue: 'Quality',
                    })}
                    value={quality}
                    onChange={setQuality}
                  />
                  <AxisInput
                    id="eval-d"
                    label={t('supplier.eval.axis.DELIVERY', {
                      defaultValue: 'Delivery',
                    })}
                    value={delivery}
                    onChange={setDelivery}
                  />
                  <AxisInput
                    id="eval-a"
                    label={t('supplier.eval.axis.AUDIT', {
                      defaultValue: 'Audit',
                    })}
                    value={audit}
                    onChange={setAudit}
                  />
                  <AxisInput
                    id="eval-s"
                    label={t('supplier.eval.axis.SERVICE', {
                      defaultValue: 'Service',
                    })}
                    value={service}
                    onChange={setService}
                  />

                  {isDisqualPreview && (
                    <div className={styles.fullSpan}>
                      <label htmlFor="eval-rat" className={styles.formLabel}>
                        {t('supplier.eval.disqualRationale', {
                          defaultValue:
                            'Disqualification rationale (min 200 chars)',
                        })}
                        <span className={styles.requiredStar} aria-hidden="true">*</span>
                      </label>
                      <textarea
                        id="eval-rat"
                        className={styles.dialogTextarea}
                        value={rationale}
                        rows={5}
                        onChange={(e) => setRationale(e.target.value)}
                        aria-describedby="eval-rat-counter"
                      />
                      <div
                        id="eval-rat-counter"
                        className={styles.charCounter}
                        aria-live="polite"
                      >
                        {t('supplier.eval.disqualCounter', {
                          defaultValue: '{{len}} / {{min}} characters',
                          len: nf.format(rationale.trim().length),
                          min: nf.format(RATIONALE_MIN),
                        })}
                      </div>
                    </div>
                  )}

                  <div className={styles.formFooter}>
                    <Button
                      type="submit"
                      variant="secondary"
                      isLoading={computeM.isPending}
                    >
                      {t('supplier.eval.saveDraft', {
                        defaultValue: 'Save draft',
                      })}
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => setShowSign(true)}
                    >
                      {t('supplier.eval.sign.open', {
                        defaultValue: 'Sign attestation',
                      })}
                    </Button>
                  </div>
                </form>
              </CardBody>
            </Card>
          )}

          {isSigned && data.signers.length > 0 && (
            <Card>
              <CardHeader>
                <h2 className={wstyles.sectionTitle}>
                  {t('supplier.eval.signersTitle', {
                    defaultValue: 'Signers',
                  })}
                </h2>
              </CardHeader>
              <CardBody>
                <ul className={styles.signersList}>
                  {data.signers.map((s, idx) => (
                    <li key={idx} className={styles.signersItem}>
                      <strong>{s.name}</strong>
                      <span className={styles.signersMeaning}>{s.meaning}</span>
                      <time
                        dateTime={s.signed_at_utc}
                        className={styles.signersTs}
                      >
                        {dateFmt.format(new Date(s.signed_at_utc))}
                      </time>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          )}

          {/* Performance charts — weighted-score trend + current axis breakdown */}
          <Card>
            <CardHeader>
              <h2 className={wstyles.sectionTitle}>
                {t('supplier.eval.perfTitle', { defaultValue: 'Performance trend' })}
              </h2>
            </CardHeader>
            <CardBody>
              <div className={styles.chartRow}>
                <div className={styles.chartCell}>
                  <h3 className={styles.chartLabel}>
                    {t('supplier.eval.perfWeighted', { defaultValue: 'Weighted score over time' })}
                  </h3>
                  {trendData.length >= 2 ? (
                    <AreaTrend
                      data={trendData}
                      xKey="period"
                      series={[{ key: 'score', name: t('supplier.eval.history.weighted', { defaultValue: 'Weighted score' }), color: 'var(--spectro-purple, #9333EA)' }]}
                      ariaLabel={t('supplier.eval.perfWeighted', { defaultValue: 'Weighted score over time' })}
                      height={220}
                      valueFormatter={(v) => (typeof v === 'number' ? nf.format(v) : String(v ?? ''))}
                    />
                  ) : (
                    <p className={styles.tabEmpty}>
                      {t('supplier.eval.perfTrendEmpty', { defaultValue: 'Not enough history for a trend yet.' })}
                    </p>
                  )}
                </div>
                <div className={styles.chartCell}>
                  <h3 className={styles.chartLabel}>
                    {t('supplier.eval.perfAxes', { defaultValue: 'Current axis scores' })}
                  </h3>
                  <BarCompare
                    data={axisData}
                    xKey="axis"
                    series={[{ key: 'score', name: t('supplier.eval.perfScore', { defaultValue: 'Score' }) }]}
                    colorByPoint
                    ariaLabel={t('supplier.eval.perfAxes', { defaultValue: 'Current axis scores' })}
                    height={220}
                    valueFormatter={(v) => (typeof v === 'number' ? nf.format(v) : String(v ?? ''))}
                  />
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h2 className={wstyles.sectionTitle}>
                {t('supplier.eval.historyTitle', {
                  defaultValue: 'Historical scorecards',
                })}
              </h2>
            </CardHeader>
            <CardBody>
              <HistoryTable rows={data.history} />
            </CardBody>
          </Card>

          {showSign && (
            <SignAttestationDialog
              evaluationId={data.evaluation_id}
              classification={inReview ? localClass : data.classification ?? 'APPROVED'}
              initialRationale={rationale}
              onClose={() => setShowSign(false)}
              onSigned={() => {
                setShowSign(false);
                evalQ.refetch();
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * AxisInput — numeric input 0-100 with paired label.
 * --------------------------------------------------------------------------- */

interface AxisInputProps {
  id: string;
  label: string;
  value: number;
  onChange: (v: number) => void;
}

function AxisInput({ id, label, value, onChange }: AxisInputProps) {
  return (
    <div className={styles.axisField}>
      <label htmlFor={id} className={styles.formLabel}>
        {label}
      </label>
      <input
        id={id}
        type="number"
        min={0}
        max={100}
        step={1}
        inputMode="numeric"
        className={styles.axisInput}
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => {
          const next = Number(e.target.value);
          if (!Number.isFinite(next)) return;
          onChange(Math.max(0, Math.min(100, next)));
        }}
        aria-label={label}
      />
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * HistoryTable — past scorecards for the same supplier.
 * --------------------------------------------------------------------------- */

function HistoryTable({ rows }: { rows: HistoricalScorecard[] }) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );

  if (rows.length === 0) {
    return (
      <p className={styles.tabEmpty}>
        {t('supplier.eval.history.empty', {
          defaultValue: 'No historical scorecards yet.',
        })}
      </p>
    );
  }

  return (
    <table
      className={styles.historyTable}
      aria-label={t('supplier.eval.history.tableAria', {
        defaultValue: 'Historical scorecards',
      })}
    >
      <thead>
        <tr>
          <th scope="col">
            {t('supplier.eval.history.period', { defaultValue: 'Period' })}
          </th>
          <th scope="col">
            {t('supplier.eval.history.weighted', {
              defaultValue: 'Weighted score',
            })}
          </th>
          <th scope="col">
            {t('supplier.eval.history.class', {
              defaultValue: 'Classification',
            })}
          </th>
          <th scope="col">
            {t('supplier.eval.history.status', { defaultValue: 'Status' })}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.evaluation_id}>
            <td>
              {dateFmt.format(new Date(r.period_start_utc))} —{' '}
              {dateFmt.format(new Date(r.period_end_utc))}
            </td>
            <td>
              {r.weighted_score == null ? (
                <span className={styles.subdued}>—</span>
              ) : (
                <span className={styles.historyScore}>{r.weighted_score}</span>
              )}
            </td>
            <td>
              {r.classification
                ? t(`supplier.evaluation.classification.${r.classification}`, {
                    defaultValue: r.classification,
                  })
                : '—'}
            </td>
            <td>
              {t(`supplier.eval.status.${r.status}`, {
                defaultValue: r.status,
              })}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
